/**
 * Shared formatters for chart axes, ticks and tooltips.
 *
 * Chart values arriving from the API are not guaranteed to be numbers: aggregate SQL columns
 * (SUM/COUNT over numeric or bigint) serialize to JSON as strings. Recharts needs real numbers
 * to build a numeric scale — handed strings it falls back to categorical ticks, which renders
 * as malformed, out-of-order axis labels. Always run values through `toChartNumber` before
 * passing data to a chart.
 */

export type ChartValueFormat = 'number' | 'currency' | 'percentage';

/**
 * Coerce an unknown chart value to a finite number, defaulting to 0.
 */
export const toChartNumber = (value: unknown): number => {
  const numeric = typeof value === 'number' ? value : Number(value);

  return Number.isFinite(numeric) ? numeric : 0;
};

/**
 * Coerce the configured Y-axis columns of every row to numbers, leaving other keys untouched.
 */
export const coerceChartValues = <T extends Record<string, unknown>>(
  data: T[],
  valueColumns: string[]
): T[] =>
  data.map((row) => {
    const coerced: Record<string, unknown> = { ...row };

    valueColumns.forEach((column) => {
      if (column in row) {
        coerced[column] = toChartNumber(row[column]);
      }
    });

    return coerced as T;
  });

/**
 * Compact currency for axis ticks — full amounts are far too wide for an axis at
 * contract-portfolio scale (e.g. $12,389,308,126 becomes $12.4B).
 */
export const formatCompactCurrency = (value: unknown, currency: string = 'USD'): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    notation: 'compact',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(toChartNumber(value));

/**
 * Full-precision currency, for tooltips where the exact amount matters.
 */
export const formatFullCurrency = (value: unknown, currency: string = 'USD'): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(toChartNumber(value));

/**
 * Percentages come from SQL ratios and can carry many decimals; an unrounded label would
 * overflow the axis width. Keep at most one decimal and drop a trailing ".0".
 */
export const formatPercentage = (value: unknown): string => {
  const numeric = toChartNumber(value);

  return Number(numeric.toFixed(1)).toLocaleString();
};

/**
 * Format an axis tick according to the configured value format.
 */
export const formatAxisTick = (value: unknown, format: ChartValueFormat = 'number'): string => {
  const numeric = toChartNumber(value);

  if (format === 'currency') {
    return formatCompactCurrency(numeric);
  }

  if (format === 'percentage') {
    return `${formatPercentage(numeric)}%`;
  }

  return numeric.toLocaleString();
};

/**
 * Format a tooltip value: currency shows the exact amount rather than the compact tick form.
 */
export const formatTooltipValue = (value: unknown, format: ChartValueFormat = 'number'): string => {
  const numeric = toChartNumber(value);

  if (format === 'currency') {
    return formatFullCurrency(numeric);
  }

  if (format === 'percentage') {
    return `${formatPercentage(numeric)}%`;
  }

  return numeric.toLocaleString();
};

/** Maximum characters shown on a category axis tick before truncation. */
const CATEGORY_TICK_MAX_CHARS = 18;

/**
 * Truncate a long category label for display; the full text stays available via the tooltip.
 */
export const truncateCategoryLabel = (
  label: unknown,
  maxChars: number = CATEGORY_TICK_MAX_CHARS
): string => {
  const text = String(label ?? '');

  return text.length > maxChars ? `${text.slice(0, maxChars - 1)}…` : text;
};
